import axios, { type AxiosError, type AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { API_URL, UAE_PASS_LOGOUT_URL } from '@/const/env';
import { tokenStore } from '@/auth/token';
import { API_ROUTES } from './routes';
import { SignOutReason } from '@/types/auth';
import { APP_ROUTES } from '@/const/routes';

// Lazy import to avoid circular dependency with router
let _router: { navigate: (opts: { to: string; search?: Record<string, string>; replace?: boolean }) => void } | null = null;
export const setRouter = (router: typeof _router) => {
  _router = router;
};

export const http = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Attach Bearer token to every request (except the refresh endpoint itself)
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  const existingAuth = (config.headers as AxiosHeaders).get('Authorization');
  const hasBearerToken =
    typeof existingAuth === 'string' && existingAuth.startsWith('Bearer ');

  if (token && config.url && !config.url.includes(API_ROUTES.authentication.refresh) && !hasBearerToken) {
    (config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
  }

  return config;
});

// ─── Token refresh state ─────────────────────────────────────────────────────
let isRefreshing = false;
type Subscriber = { resolve: (token: string) => void; reject: (err: unknown) => void };
let refreshSubscribers: Subscriber[] = [];

function notifySubscribers(newToken: string) {
  refreshSubscribers.forEach((s) => s.resolve(newToken));
  refreshSubscribers = [];
}

function rejectSubscribers(err: unknown) {
  refreshSubscribers.forEach((s) => s.reject(err));
  refreshSubscribers = [];
}

async function forceLogout() {
  const savedPath = `${window.location.pathname}${window.location.search}`;
  sessionStorage.setItem('redirectPath', savedPath);

  tokenStore.clear();

  const { useAuthStore } = await import('@/auth/useAuthStore');
  const isLoggedInViaUAEPass = useAuthStore.getState().isLoggedInViaUAEPass;
  useAuthStore.getState().clearAuth();

  if (isLoggedInViaUAEPass) {
    window.location.href = UAE_PASS_LOGOUT_URL;
  } else if (_router) {
    _router.navigate({ to: APP_ROUTES.login, search: { reason: SignOutReason.SESSION_EXPIRED }, replace: true });
  } else {
    window.location.href = `${APP_ROUTES.login}?reason=${SignOutReason.SESSION_EXPIRED}`;
  }
}

// ─── Response interceptor ────────────────────────────────────────────────────
http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestURL = originalConfig?.url ?? null;

    const isAuthEndpoint =
      requestURL === API_ROUTES.authentication.login ||
      requestURL === API_ROUTES.authentication.logout ||
      requestURL === API_ROUTES.authentication.refresh;

    // Only intercept 401s on non-auth, non-retried requests
    if (status !== 401 || isAuthEndpoint || originalConfig._retry) {
      return Promise.reject(error);
    }

    // Another refresh is already in flight — queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshSubscribers.push({ resolve, reject });
      })
        .then((newToken) => {
          (originalConfig.headers as AxiosHeaders).set('Authorization', `Bearer ${newToken}`);
          originalConfig._retry = true;
          return http(originalConfig);
        })
        .catch(() => Promise.reject(error));
    }

    // First 401 — attempt a token refresh
    originalConfig._retry = true;
    isRefreshing = true;

    try {
      const { authApi } = await import('./auth.api');
      const { accessToken } = await authApi.refresh();

      tokenStore.set(accessToken);
      (originalConfig.headers as AxiosHeaders).set('Authorization', `Bearer ${accessToken}`);

      notifySubscribers(accessToken);
      isRefreshing = false;

      return http(originalConfig);
    } catch (refreshError) {
      rejectSubscribers(refreshError);
      isRefreshing = false;

      await forceLogout();
      return Promise.reject(refreshError);
    }
  },
);

export default http;
